import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, makeStyles } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import { parseISO } from 'date-fns';

import usePermission from '../../hooks/usePermission';
import { usePlanPermissions } from '../../context/PlanPermissionsContext.jsx';
import { getNextAppointment } from '../../services/appointmentService';
import { formatAppointmentCountdown } from '../../utils/appointmentCountdown';
import AppointmentQuickViewModal from '../AppointmentQuickViewModal';

const POLL_INTERVAL_MS = 60000;

const useStyles = makeStyles((theme) => ({
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '5px 12px',
    borderRadius: 20,
    background: '#f1f5f9',
    transition: 'background 0.2s',
    '&:hover': { background: '#e2e8f0' },
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  title: {
    fontWeight: 600,
    fontSize: '0.82rem',
    color: '#0f172a',
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  countdown: { fontWeight: 700, fontSize: '0.82rem', color: '#3b82f6', whiteSpace: 'nowrap' },
}));

/**
 * Aviso do próximo compromisso na barra superior — mesmo padrão do
 * `TeamGoalChip`, mas apontando pro compromisso em vez da meta do CRM.
 *
 * Some quando: sem plano de Agenda, sem permissão, config da empresa
 * desligada, ou sem compromisso futuro — nunca fica vazio ocupando espaço.
 */
export default function NextMeetingChip() {
  const classes = useStyles();
  const history = useHistory();
  const { allowed } = usePermission('agenda:read');
  const { appointments: planAppointments } = usePlanPermissions();
  const [result, setResult] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const canUse = allowed && planAppointments;

  useEffect(() => {
    if (!canUse) return undefined;
    let active = true;
    const load = () =>
      getNextAppointment()
        .then((data) => {
          if (active) setResult(data);
        })
        .catch(() => {});
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [canUse]);

  // Recalcula "em X min" mesmo sem novo fetch — só busca de novo a cada minuto,
  // mas o texto não pode ficar parado esse tempo todo.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);

  if (!canUse || !result?.enabled || !result?.appointment) return null;

  const { appointment } = result;
  const start = parseISO(appointment.startDatetime);

  return (
    <>
      <Box className={classes.chip} onClick={() => setDetailsOpen(true)} title={appointment.title}>
        <EventIcon style={{ fontSize: 18, color: '#3b82f6' }} />
        <Typography className={classes.title}>{appointment.title}</Typography>
        <Typography className={classes.countdown}>
          {formatAppointmentCountdown(start, now)}
        </Typography>
      </Box>
      <AppointmentQuickViewModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        appointment={appointment}
        onViewAppointments={() => {
          setDetailsOpen(false);
          history.push('/book/appointments');
        }}
      />
    </>
  );
}
